<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">Blacklist</h3>
        <div class="text-xs-right">
          <v-btn
              @click="showAddDialog"
              color="blue darken-3"
              dark
              fab
              v-if="$oauth.checkIfHasPermission(['alter blacklist'])"
              x-small>
            <v-icon>add</v-icon>
          </v-btn>
          <export-button
              class="ml-2"
              v-if="$oauth.checkIfHasPermission(['export blacklist'])"/>
          <refresh-button :click-function="getAll"></refresh-button>
        </div>
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-layout class="py-1 mx-2" row wrap>
            <v-flex class="pb-0" xs3>
              <v-text-field
                  :elevation="0"
                  flat
                  hide-details
                  placeholder="Search by typing here..."
                  prepend-inner-icon="search"
                  solo
                  v-model="filters.query"
                  @keypress.enter="handleFilterChange"
              />
            </v-flex>
            <v-flex class="pb-0" xs3>
              <custom-date-time-range-picker
                  @change="handleFilterChange"
                  @input="handleFilterChange"
                  v-model="filters.date_time_range"
              />
            </v-flex>
            <v-flex class="pb-0" xs3>
              <v-autocomplete
                  :elevation="0"
                  flat
                  hide-details
                  prepend-inner-icon="filter_list"
                  clearable
                  :items="blacklist_categories"
                  placeholder="Filter by blacklist category"
                  v-model="filters.blacklist_category"
                  solo
                  @change="handleFilterChange"
              />
            </v-flex>
            <v-flex xs3 class="pb-0">
              <v-autocomplete
                  :items="status_options"
                  flat
                  hide-details
                  label="Status"
                  prepend-inner-icon="filter_list"
                  solo
                  @change="handleFilterChange"
                  v-model="filters.status"
              ></v-autocomplete>
            </v-flex>
          </v-layout>
          <v-divider/>

          <v-card-text class="pa-0">
            <v-data-table
                :footer-props="footerProps"
                :headers="headers"
                :items="blacklist_data"
                :loading="loading"
                :options.sync="pagination"
                :server-items-length="totalNumber"
                item-key="id"
            >
              <template v-slot:item.document_file="{ item }">
                <file-display :src="item.document_file"></file-display>
              </template>
              <template v-slot:item.deactivated_file="{ item }" >
                <file-display v-if="item.deactivated_file" :src="item.deactivated_file"></file-display>
                <span v-else>N/A</span>
              </template>
              <template v-slot:item.action="{ item }">
                <v-tooltip top v-if="$oauth.checkIfHasPermission(['read blacklist'])">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="view(item)"
                        color="primary"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View</span>
                </v-tooltip>
                <v-tooltip top v-if="$oauth.checkIfHasPermission(['alter blacklist'])">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="edit(item)"
                        color="primary"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>edit</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip top v-if="$oauth.checkIfHasPermission(['alter blacklist'])">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="changeStatus(item)"
                        class="ml-2"
                        color="red lighten-1"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>{{
                          filters.status ===
                          'active' ? 'mdi-close-circle' : 'mdi-restore'
                        }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ filters.status === 'active' ? 'De-activate' : 'Restore' }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
        persistent
        transition="dialog-bottom-transition"
        v-if="$oauth.checkIfHasPermission(['alter blacklist'])"
        v-model="form"
        :width="formData.isMultiple ? '920': '700'"
    >
      <v-card>
        <v-toolbar color="blue darken-3" dark>
          <v-toolbar-title>{{ formData.id ? 'Edit' : 'New' }} {{ title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-container grid-list-md class="pb-0">
            <v-layout wrap>
              <FormErrorDisplayHandler :errors="errors"/>
              <v-flex xs12>
                <v-form class="pa-2" lazy-validation ref="formRef" v-model="valid">
                  <v-layout row wrap>
                    <v-flex xs6 class="py-0">
                      <v-checkbox label="Add group of blacklists" v-model="formData.isMultiple"
                                  @click="showMultipleTraveller"></v-checkbox>
                    </v-flex>
                    <v-flex xs1 class="py-0 mt-4">
                      <v-btn
                          @click="addMultipleTraveller"
                          class="pull-right"
                          color="primary"
                          fab
                          v-if="formData.isMultiple"
                          x-small
                      >
                        <v-icon>add</v-icon>
                      </v-btn>
                    </v-flex>
                    <v-flex xs6 class="py-0" v-if="!formData.isMultiple">
                      <v-text-field label="Name" outlined type="text"
                                    v-model="formData.name"/>
                    </v-flex>
                    <v-flex xs6 class="py-0" v-if="!formData.isMultiple">
                      <v-text-field
                          label="Document Number"
                          outlined
                          type="text"
                          v-model="formData.document_number"
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap v-if="multiple_blacklists_travellers.length > 0"
                            v-for="(item,index) in multiple_blacklists_travellers">
                    <v-flex xs3 class="py-0">
                      <v-text-field label="Name" outlined type="text"
                                    v-model="item.name"/>
                    </v-flex>
                    <v-flex xs4 class="py-0">
                      <v-text-field
                          label="Document Number"
                          outlined
                          type="text"
                          v-model="item.document_number"
                      />
                    </v-flex>
                    <v-flex xs4 class="py-0">
                      <v-autocomplete
                          :items="nationalities"
                          item-text="country_name"
                          item-value="id"
                          label="Select Country "
                          outlined
                          v-model="item.country_id"
                      />
                    </v-flex>
                    <v-flex xs1 class="py-0" v-if="index >= 2">
                      <v-btn
                          @click="multiple_blacklists_travellers.splice(index,1)"
                          color="red darken-3 white--text"
                          fab
                          x-small
                      >
                        <v-icon>remove</v-icon>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs6 class="py-0" v-if="!formData.isMultiple">
                      <v-autocomplete
                          :items="nationalities"
                          item-text="country_name"
                          item-value="id"
                          label="Select Country "
                          outlined
                          v-model="formData.country_id"
                      />
                    </v-flex>
                    <v-flex xs6 class="py-0">
                      <v-autocomplete
                          :items="blacklist_categories"
                          :rules="[rules.required]"
                          outlined
                          label="Blacklist category *"
                          v-model="formData.blacklist_category"
                      />
                    </v-flex>
                  </v-layout>
                  <v-textarea label="Reason for Blacklisting *"
                              :rules="[rules.required]"
                              outlined
                              v-model="formData.remarks"
                  />
                  <v-textarea label="Action to be taken *"
                              :rules="[rules.required]"
                              outlined
                              v-model="formData.action_to_be_taken"
                  />
                  <v-textarea label="Hints" outlined type="textarea"
                              v-model="formData.hints"/>
                  <OutlinedDatePicker label="Expiry Date"
                                      v-model="formData.to_date"/>

                  <file-input label="File Attachment (If Any)"
                              :fileLink="formData.id ? imageFile : null"
                              :file-size="2048"
                              v-model="formData.document_file"/>
                </v-form>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn
              @click="close"
              class="white--text"
              color="red lighten-1"
          >Cancel
          </v-btn>
          <v-btn
              :loading="loading"
              @click="saveModel"
              class="white--text"
              color="blue darken-3"
          >Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="blacklistModal" width="700">
      <v-card>
        <v-card-title class="headline blue darken-3 white--text" default-title>
          {{ filters.status === 'active' ? 'De-activate BlackList' : 'Restore Blacklist' }}
        </v-card-title>
        <v-card-text class="pa-0">
          <v-container grid-list-md>
            <v-layout wrap>
              <form-error-display-handler :errors="errors"/>
              <v-flex xs12>
                <v-form ref="blacklistForm" v-model="valid" lazy-validation class="px-2">
                  <v-textarea
                      label="Remarks *"
                      outlined
                      v-model="blacklist.remarks"
                      :rules="[rules.required]"
                  />

                  <multiple-file-upload
                      label="Upload documents *"
                      v-model="blacklist.files"
                      :required="true"
                  />
                </v-form>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn
              color="red lighten-1"
              class="white--text"
              @click="blacklist={}; blacklistModal=false"
          >Close
          </v-btn>
          <v-btn color="blue darken-3" :loading="loading" class="white--text" @click="blacklistConfirm">
            {{ filters.status === 'active' ? 'De-activate' : 'Restore' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>

</template>
<script>
import Http from "axios";
import {API_URL, VERSION} from "../../config/config.js";
import OutlinedDatePicker from "../../components/OutlinedDatePicker";
import {validationMixin} from "../../mixins/validationMixin";
import {commonListMixin} from "../../mixins/commonListMixin";
import FileInput from "../../components/FileInput";
import FileDisplay from "../../components/FileDisplay";
import FormErrorDisplayHandler from "../../components/FormErrorDisplayHandler";
import MultipleFileUpload from "../../components/MultipleFileUpload";
import moment from 'moment';
import {buildFormData} from "../../helpers/FormedData";
import RefreshButton from "@/components/RefreshButton";
import {debounce} from "lodash";
import CustomDateTimeRangePicker from "../../components/CustomDateTimeRangePicker.vue";


export default {
  components: {
    CustomDateTimeRangePicker,
    RefreshButton,
    OutlinedDatePicker, FileInput, FileDisplay,
    FormErrorDisplayHandler, MultipleFileUpload
  },
  data() {
    return {
      title: "Blacklist Data",
      nationalities: [],
      model: "blacklists",
      blacklist_categories: [],
      multiple_blacklists_travellers: [],
      headers: [
        {
          text: 'S.N.',
          value: 's_no',
          sortable: false
        },
        {
          text: "Name",
          value: "name"
        },
        {
          text: "Country",
          value: "country_name"
        },
        {
          text: "Blacklist category",
          value: "blacklist_category"
        },
        // {
        //   text: "Document Number",
        //   value: "document_number"
        // },
        // {
        //   text: "Document File",
        //   value: "document_file"
        // },
        // {
        //   text: "Remarks",
        //   value: "remarks"
        // },

        {
          text: "To Date",
          value: "expiry_date"
        },
        {
          text: "Added By",
          value: "creator"
        },
        {
          text: "Created Date",
          value: "created_at"
        }
      ],
      imageFile: null,
      status_options: [
        {
          text: "Active",
          value: "active"
        },
        {
          text: "Inactive",
          value: "inactive"
        }
      ],
      blacklistModal: false,
      blacklist: {},
      pagination: {
        itemsPerPage: 25,
        sortBy: ["created_at"],
        sortDesc: ["true"]
      },
      // multipleTraveller:[],
    };
  },

  mixins: [validationMixin, commonListMixin],
  computed: {
    blacklist_data() {
      return this.$store.state.CommonList.all;
    },
  },
  methods: {
    handleFilterChange: debounce(function () {
      if (this.filters && this.filters.status == 'inactive'){
        this.headers = [];
        this.   headers =  [
          {
            text: 'S.N.',
            value: 's_no',
            sortable: false
          },
          {
            text: "Name",
            value: "name"
          },
          {
            text: "Country",
            value: "country_name"
          },
          {
            text: "Blacklist category",
            value: "blacklist_category"
          },
          {
            text: "Document Number",
            value: "document_number"
          },
          {
            text: "Document File",
            value: "document_file"
          },
          {
            text: "Remarks",
            value: "remarks"
          },

          {
            text: "To Date",
            value: "expiry_date"
          },
          {
            text: "Added By",
            value: "creator"
          },
          {
            text: "Created Date",
            value: "created_at"
          }
        ]
        if (this.$oauth.checkIfHasPermission(['alter blacklist']) || this.$oauth.checkIfHasPermission(['read blacklist']))
          this.headers.push(
              {
                text: "Action",
                value: "action",
                sortable: false
              })
        this.headers.splice(8,1,
          {
            text: "Deactivated File",
            value: "deactivated_file"
          },
          {
            text: "Deactivated Remarks",
            value: "deactivated_remarks"
          },
          {
            text: "Deactivated By",
            value: "deactivated_by"
          },
        )
      }
      this.getAll();
    }, 1000),
    showMultipleTraveller() {
      if (this.formData.isMultiple) {
        this.multiple_blacklists_travellers = [
          {
            name: null,
            document_number: null,
            country_id: null
          },
          {
            name: null,
            document_number: null,
            country_id: null
          }
        ]
      } else {
        this.multiple_blacklists_travellers = [];
      }
    },
    addMultipleTraveller() {
      this.multiple_blacklists_travellers.push({
        name: null,
        document_number: null,
        country_id: null
      });
    },
    loadNationalities() {
      Http.get(API_URL + VERSION + "/countries")
          .then(response => {
            this.nationalities = response.data.data;
          })
    },
    saveModel() {
      if (this.$refs.formRef.validate()) {
        let formData = new FormData();
        this.formData.multipleTraveller = this.multiple_blacklists_travellers;
        buildFormData(formData, this.formData);
        this.modelData = formData;
        this.save(this.title).then(r => {
          this.multiple_blacklists_travellers = [];
        })
      }
    },

    edit(item) {
      this.formData = JSON.parse(JSON.stringify(item));
      this.imageFile = item.document_file;
      this.formData.to_date = moment(item.expiry_date, 'YYYY-MM-DD', true).isValid() ? item.expiry_date : '';
      this.form = true;
    },
    view(item) {
      this.$router.push({ name: 'BlacklistDetail', params: { id: item.id } });
    },
    changeStatus(item) {
      this.blacklistModal = true;
      this.blacklist.id = item.id;
    },
    blacklistConfirm() {
      if (this.$refs.blacklistForm.validate()) {
        let formData = new FormData();
        formData.append('id', this.blacklist.id);
        formData.append('remarks', this.blacklist.remarks);
        let files = this.blacklist.files;
        if (files !== undefined)
          for (let i = 0; i < files.length; i++) {
            formData.append(`files[${i}]`, files[i]);
          }
        Http.post(API_URL + VERSION + "/blacklists/change-status", formData)
            .then(() => {
              this.blacklistModal = false;
              this.blacklist = {};
              this.$store.commit("Notification/showSuccess", "Black-list Record status changed successfully");
              this.getAll();
            })
            .catch(() => {
              this.$store.commit("Notification/showError", "Something went wrong. Please try again later")
            });
      }
    }


  },
  created() {
    if (this.$oauth.checkIfHasPermission(['alter blacklist']) || this.$oauth.checkIfHasPermission(['read blacklist']))
      this.headers.push(
          {
            text: "Action",
            value: "action",
            sortable: false
          })
  },
  mounted() {
    this.$store.commit("CommonList/setModel", "blacklists");
    this.loadNationalities();
    Http.get(`${API_URL}${VERSION}` + '/dynamic-values-setup/fetch-all', {
      params: {
        key: 'BlackListReason',
      }
    }).then((res) => {
      let items = res.data.map((item) => item.value);
      items = items.map(i => i.name);
      this.blacklist_categories = items;
    });
    this.filters.status = 'active';
  },
};
</script>
